import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { tvApi, useTVAuth } from './TVContext';
import { focusFirst, useDpadNavigation } from './focus';

const TVDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const trailerPreferred = searchParams.get('trailer') === '1';
  const navigate = useNavigate();
  const { user } = useTVAuth();
  const [content, setContent] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const reviewsTimer = useRef(null);

  useDpadNavigation(() => navigate('/tv/browse'));

  useEffect(() => {
    const load = async () => {
      try {
        const res = await tvApi.get(`/content/${id}`);
        setContent(res.data);
      } catch (e) {
        setContent(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const loadReviews = async () => {
    try {
      const res = await tvApi.get(`/video-reviews/${id}`);
      setReviews(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.debug('Loading reviews failed:', e);
    }
  };

  useEffect(() => {
    loadReviews();
    reviewsTimer.current = setInterval(loadReviews, 60000);
    return () => reviewsTimer.current && clearInterval(reviewsTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { if (!loading) focusFirst(); }, [loading]);

  if (loading) return <div className="tv-root tv-loading" data-testid="tv-detail-loading">Loading…</div>;
  if (!content) return <div className="tv-root tv-loading" data-testid="tv-detail-error">Content not found. Press Back.</div>;

  const title = content.title || content.name;
  const poster = content.thumbnail || content.poster;
  const locked = content.is_locked;
  const isMusic = content.content_type === 'music_video';
  const hasTrailer = !!content.trailer_playback_id;
  const mainPlaybackId = content.playback_id || content.mux_playback_id;
  const needsSubscription = content.is_subscription_required || content.is_premium;
  // Policy: music videos free, trailers free, everything else needs premium
  const canWatchMain = !locked && mainPlaybackId && (isMusic || (user && user.is_premium) || !needsSubscription);

  const playMain = () => {
    if (canWatchMain) navigate(`/tv/watch/${id}?type=main`);
  };
  const playTrailer = () => {
    if (hasTrailer) navigate(`/tv/watch/${id}?type=trailer`);
  };

  return (
    <div className="tv-root" data-testid="tv-detail">
      <div className="tv-detail-hero">
        {poster && <img src={poster} alt={title} />}
        <div className="tv-detail-hero-overlay">
          <div style={{ fontSize: 18, color: '#1FA2FF', fontWeight: 700, letterSpacing: 2 }}>
            {(content.content_type || '').toUpperCase().replace('_', ' ')}
          </div>
          <div className="tv-featured-title" data-testid="tv-detail-title">{title}</div>
          <div className="tv-featured-desc" style={{ maxWidth: '65%' }}>{content.description}</div>
          {locked && content.release_date && (
            <div style={{ color: '#ffb3b3', fontSize: 18 }}>🔒 Releases {new Date(content.release_date).toLocaleString()}</div>
          )}
          <div className="tv-detail-actions">
            {canWatchMain && (
              <button
                className="tv-btn tv-focusable"
                data-testid="tv-detail-play-btn"
                onClick={playMain}
                autoFocus
              >▶ Play</button>
            )}
            {!canWatchMain && needsSubscription && !locked && (
              <button
                className="tv-btn tv-btn-ghost tv-focusable"
                data-testid="tv-detail-premium-btn"
                onClick={() => navigate('/tv')}
              >🔒 Sign in with premium to play</button>
            )}
            {hasTrailer && (trailerPreferred || !canWatchMain) && (
              <button
                className="tv-btn tv-btn-ghost tv-focusable"
                data-testid="tv-detail-trailer-btn"
                onClick={playTrailer}
                autoFocus={trailerPreferred}
              >▶ Play Trailer</button>
            )}
            {hasTrailer && canWatchMain && !trailerPreferred && (
              <button
                className="tv-btn tv-btn-ghost tv-focusable"
                data-testid="tv-detail-trailer-btn"
                onClick={playTrailer}
              >Play Trailer</button>
            )}
            <button
              className="tv-btn tv-btn-ghost tv-focusable"
              data-testid="tv-detail-back-btn"
              onClick={() => navigate('/tv/browse')}
            >◀ Back</button>
          </div>
        </div>
      </div>

      <div className="tv-reviews-list" data-testid="tv-reviews">
        <h2>Live Reviews {reviews.length > 0 && <span style={{ fontSize: 18, color: '#6f8098', fontWeight: 500 }}>(auto-refresh 60s • read-only on TV)</span>}</h2>
        {reviews.length === 0 && (
          <div style={{ color: '#6f8098', fontSize: 20 }}>No reviews yet. Reviews posted from phone or web will appear here.</div>
        )}
        {reviews.slice(0, 12).map((r) => (
          <div className="tv-review-card" key={r.id} data-testid={`tv-review-${r.id}`}>
            {r.user_avatar ? <img src={r.user_avatar} alt={r.username} /> : <div style={{ width: 44, height: 44, borderRadius: 22, background: '#1f2937' }} />}
            <div className="tv-review-body">
              <div className="tv-review-name">{r.username || 'Member'}</div>
              {r.caption && <div className="tv-review-text">{r.caption}</div>}
              {r.text && <div className="tv-review-text">{r.text}</div>}
              {r.rating && <div className="tv-review-text">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</div>}
              <div className="tv-review-meta">{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TVDetail;
